import { useCallback, useEffect, useState } from 'react';
import { backend } from './data';
import type { Membership, PasswordStatus, Session } from '@bhorosha/data';
import { t, tErr } from '@bhorosha/i18n';
import { AuthCard, ErrorText, Notice, Spinner } from './ui/components';
import { AuthScreen, ResetPasswordScreen } from './screens/AuthScreen';
import { InviteScreen, PENDING_INVITE_KEY, inviteTokenFromUrl } from './screens/InviteScreen';
import { Onboarding } from './screens/Onboarding';
import { FamilyApp } from './screens/FamilyApp';
import { AdminConsole } from './screens/AdminConsole';
import { ManualPage } from './screens/ManualPage';
import { SetupAdminScreen, TempPasswordScreen, WelcomeScreen, welcomeTokenFromUrl } from './screens/SetupScreens';

const activeKey = (uid: string) => `bh.activeFamily.${uid}`;

/**
 * Decides which screen to show:
 *   /manual                  -> user guide (no sign-in)
 *   /welcome/<token>         -> set password from the admin's invite
 *   no super admin yet       -> one-time "Set up Bhorosha" (create the admin login)
 *   not signed in            -> sign in
 *   one-time password        -> choose own password first
 *   super admin              -> admin console
 *   opened a family invite   -> accept invite
 *   signed in, no family yet -> create family
 *   otherwise                -> the app, for the chosen family
 */
export function App() {
  if (location.pathname.replace(/\/$/, '') === '/manual') return <ManualPage />;
  return <MainApp />;
}

function MainApp() {
  const [welcome, setWelcome] = useState<string | null>(welcomeTokenFromUrl);
  const [adminExists, setAdminExists] = useState<boolean | undefined>(undefined);
  const [isAdmin, setIsAdmin] = useState<boolean | undefined>(undefined);
  const [pwStatus, setPwStatus] = useState<PasswordStatus | undefined>(undefined);
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [recovery, setRecovery] = useState(location.pathname === '/reset-password');
  const [memberships, setMemberships] = useState<Membership[] | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [invite, setInvite] = useState<string | null>(() => {
    const fromUrl = inviteTokenFromUrl();
    if (fromUrl) localStorage.setItem(PENDING_INVITE_KEY, fromUrl);
    return fromUrl || localStorage.getItem(PENDING_INVITE_KEY);
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    backend.getSession().then(setSession);
    return backend.onAuthChange((s, ev) => {
      if (ev === 'PASSWORD_RECOVERY') setRecovery(true);
      setSession(prev => (prev?.userId === s?.userId && prev?.displayName === s?.displayName ? prev : s));
      if (!s) setMemberships(null);
    });
  }, []);

  const loadMemberships = useCallback(async () => {
    try {
      const list = await backend.listMemberships();
      setMemberships(list); setLoadErr('');
      return list;
    } catch (e) { setLoadErr(tErr(e)); return []; }
  }, []);
  useEffect(() => { backend.adminExists().then(setAdminExists).catch(() => setAdminExists(true)); }, [session]);
  useEffect(() => {
    if (!session) { setIsAdmin(undefined); setPwStatus(undefined); return; }
    backend.passwordStatus().then(setPwStatus).catch(() => setPwStatus('none'));
  }, [session]);
  useEffect(() => {
    if (!session || pwStatus !== 'none') { setIsAdmin(undefined); return; }
    backend.isAppAdmin().then(a => { setIsAdmin(a); if (!a) void loadMemberships(); }).catch(() => { setIsAdmin(false); void loadMemberships(); });
  }, [session, pwStatus, loadMemberships]);

  const clearInvite = () => {
    localStorage.removeItem(PENDING_INVITE_KEY);
    setInvite(null);
    if (location.pathname !== '/') history.replaceState(null, '', '/');
  };
  const openFamily = (fid: string) => {
    if (session) localStorage.setItem(activeKey(session.userId), fid);
    location.href = '/'; // fresh page: the app screens are mounted once per family
  };

  if (welcome) return <WelcomeScreen token={welcome} onDone={() => setWelcome(null)} />;
  if (session === undefined || adminExists === undefined) return <Spinner />;
  if (!adminExists) return <SetupAdminScreen onDone={() => { setAdminExists(true); location.href = '/'; }} />;

  if (recovery && session) {
    return <ResetPasswordScreen onDone={() => { setRecovery(false); history.replaceState(null, '', '/'); }} />;
  }

  if (session && pwStatus === undefined) return <Spinner />;
  if (session && pwStatus !== 'none') {
    return <TempPasswordScreen session={session} expired={pwStatus === 'expired'} onDone={() => setPwStatus('none')} />;
  }

  if (invite) {
    return <InviteScreen token={invite} session={session} onSkip={clearInvite}
      onJoined={fid => { clearInvite(); openFamily(fid); }} />;
  }

  if (!session) return <AuthScreen />;
  if (isAdmin === undefined) return <Spinner />;
  if (isAdmin) return <AdminConsole session={session} />;

  if (loadErr) {
    return (
      <AuthCard>
        <ErrorText>{loadErr}</ErrorText>
        <button className="bh-btn bh-btn-primary bh-full" onClick={() => loadMemberships()}>{t('common.tryAgain')}</button>
      </AuthCard>
    );
  }
  if (memberships === null) return <Spinner />;

  if (!memberships.length || creating) {
    return <Onboarding session={session} onCreated={openFamily} onCancel={memberships.length ? () => setCreating(false) : undefined} />;
  }

  const saved = localStorage.getItem(activeKey(session.userId));
  const current = memberships.find(m => m.familyId === saved) || memberships[0];
  return (
    <>
      {backend.mode === 'demo' && <div className="bh-demo-banner no-print"><Notice tone="warn">{t('demo.banner')}</Notice></div>}
      <FamilyApp
        session={session}
        membership={current}
        memberships={memberships}
        onSwitchFamily={openFamily}
        onNewFamily={() => setCreating(true)}
        onMembershipsChanged={loadMemberships}
      />
    </>
  );
}
